import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLicenseGate } from "../../../hooks/useLicenseGate";
import { RootState, useAppDispatch } from "../../../store";
import { PiLockKeyLight, PiArrowRightLight, PiRocketLaunchLight, PiPlusCircleLight, PiPuzzlePieceLight, PiCrownLight, PiEnvelopeSimpleLight, PiCheckCircleLight } from "react-icons/pi";
import { useTranslation } from "react-i18next";
import Button from "../TailwindComponents/Button";
import { addAddon, verifyAddonPayment, fetchMyAccess, changePlan, requestFeatureFromAdmin, LicensePlan } from "../../../store/actions/PlanActions";
import Spinner from '../TailwindComponents/Spinner';

const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === "true";

/**
 * LicenseOverlay — wraps page content and renders a semi-transparent
 * overlay when the current route's section is not licensed.
 *
 * - Content still renders (blurred/grayed) behind the overlay so users
 *   can see what they're missing — enticing them to upgrade.
 * - All interactions are blocked (pointer-events-none on content,
 *   overlay captures clicks).
 * - The overlay itself IS the lockout UX — no modals, no toasts.
 *   A global flag (__ponsLicenseOverlayActive) tells interceptors
 *   and error handlers to suppress duplicate feedback.
 */
const LicenseOverlay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { sectionForRoute, hasSectionAccess } = useLicenseGate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);
  const plans = useSelector((state: RootState) => state.license?.plans ?? []);
  const access = useSelector((state: RootState) => state.license?.access);
  const isOrgAdmin = userInfo?.is_org_admin === true;

  const section = sectionForRoute(location.pathname);
  const isLocked = LICENSING_ENABLED && section ? !hasSectionAccess(section) : false;

  const [addonLoading, setAddonLoading] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Find the addon that unlocks this section
  const matchingAddon = useMemo(() => {
    if (!isLocked || !section) return null;
    return addons.find((a: LicensePlan) => a.feature_gates?.sections?.includes(section)) ?? null;
  }, [isLocked, section, addons]);

  // Upsell: check if upgrading to Full is cheaper than Starter + add-ons
  const upsellSuggestion = useMemo(() => {
    if (!isLocked || !matchingAddon || !isOrgAdmin) return null;

    // Only upsell from Starter
    const currentPlan = access?.license_info;
    if (!currentPlan || currentPlan.plan_type !== "base") return null;
    const isStarter = currentPlan.plan_name === "pons_starter";
    if (!isStarter) return null;

    // Find the Full plan
    const fullPlan = plans.find((p: LicensePlan) => p.name === "pons_full");
    if (!fullPlan) return null;

    // Starter plan price
    const starterPlan = plans.find((p: LicensePlan) => p.name === "pons_starter");
    if (!starterPlan) return null;

    const starterPrice = starterPlan.price_monthly;
    const fullPrice = fullPlan.price_monthly;

    // Sum up prices of currently active add-on licenses
    const activeAddonLicenseIds = new Set(
      (access?.licenses ?? [])
        .filter((l) => l.type === "addon" && l.is_active)
        .map((l) => l.license_id)
    );

    let currentAddonSpend = 0;
    for (const addon of addons) {
      if (activeAddonLicenseIds.has(addon.id)) {
        currentAddonSpend += addon.price_monthly;
      }
    }

    // Total if they add this new addon
    const projectedTotal = starterPrice + currentAddonSpend + matchingAddon.price_monthly;
    const savings = projectedTotal - fullPrice;

    // Show upsell if Full is cheaper or within €10 (close enough to recommend)
    if (savings >= -10) {
      return {
        fullPlan,
        fullPrice,
        projectedTotal,
        savings: Math.max(0, savings),
        activeAddonCount: activeAddonLicenseIds.size,
      };
    }
    return null;
  }, [isLocked, matchingAddon, isOrgAdmin, access, plans, addons]);

  // Set global flag so apis.tsx interceptors and LicenseErrorHandler
  // suppress duplicate modals / error toasts on locked pages.
  useEffect(() => {
    (window as any).__ponsLicenseOverlayActive = isLocked;
    return () => {
      (window as any).__ponsLicenseOverlayActive = false;
    };
  }, [isLocked]);

  if (!isLocked) {
    return <>{children}</>;
  }

  const SECTION_LABELS: Record<string, string> = {
    matters: "Case Management",
    clients: "Client Management",
    tasks: "Tasks & Deadlines",
    files: "Document Management",
    research: "AI Legal Research",
    tables: "AI Due Diligence Tables",
    analytics: "Analytics & Reporting",
    data_sources: "Knowledge Base (RAG)",
    templates: "Document Templates",
    client_pipeline: "Client Pipeline & AI Intake",
    finance: "Finance & Billing",
    people: "People Management",
    departments: "Departments",
    teams: "Teams",
    calendar: "Calendar",
    messages: "Message Center",
    review: "Contract Review",
    lawyers: "Lawyer Directory",
    finder: "Finder",
  };

  const sectionLabel = section ? SECTION_LABELS[section] || section : "";

  const handleAddAddon = async () => {
    if (!matchingAddon || addonLoading) return;
    const orgId = userInfo?.OrganizationID || userInfo?.organization_id;
    const email = userInfo?.Email || userInfo?.email || "";
    if (!orgId) return;

    setAddonLoading(true);
    try {
      const successUrl = `${window.location.origin}/account?tab=plans&success=true`;
      const cancelUrl = `${window.location.origin}${location.pathname}${location.search}`;
      const result = await dispatch(
        addAddon({
          addon_license_id: matchingAddon.id,
          billing_period: "monthly",
          seat_count: 1,
          email,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.requires_checkout && result?.url) {
        window.location.assign(result.url);
        return;
      }
      if (result?.payment_status === "active") {
        // Payment confirmed immediately — refresh access and reload
        await dispatch(fetchMyAccess());
        window.location.reload();
        return;
      }
      if (result?.payment_status === "pending_payment" && result?.org_license_id) {
        // Payment processing — poll for confirmation
        setPaymentPending(true);
        setAddonLoading(false);
        const orgLicenseId = result.org_license_id;
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 3000;
        const poll = async () => {
          attempts++;
          const status = await dispatch(verifyAddonPayment(orgLicenseId));
          if (status?.payment_status === "active") {
            setPaymentPending(false);
            await dispatch(fetchMyAccess());
            window.location.reload();
            return;
          }
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            // Give up polling — webhook will handle it eventually
            setPaymentPending(false);
          }
        };
        setTimeout(poll, pollInterval);
        return;
      }
      // No recognized response — reset loading
      setAddonLoading(false);
    } catch {
      setAddonLoading(false);
    }
  };

  const handleUpgradeToFull = async () => {
    if (!upsellSuggestion || upgradeLoading) return;
    setUpgradeLoading(true);
    try {
      const result = await dispatch(
        changePlan({
          new_license_id: upsellSuggestion.fullPlan.id,
          billing_period: "monthly",
        })
      );
      if (result) {
        await dispatch(fetchMyAccess());
        window.location.reload();
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="relative flex-1 min-h-0">
      {/* Page content — rendered but non-interactive */}
      <div className="pointer-events-none select-none opacity-40 blur-[1.5px]">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/30 dark:bg-gray-900/30">
        <div className="glass flex flex-col px-8 py-10 max-w-md rounded-xl shadow-lg">
          <div className="flex items-center justify-start mb-4">
            <PiLockKeyLight className="w-7 h-7 mx-auto text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("Upgrade to unlock")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            {t("Your current plan does not include")} <strong className="text-gray-700 dark:text-gray-200">{t(sectionLabel)}</strong>.{" "}
            {upsellSuggestion
              ? t("Upgrade to PONS Full to get everything — including this and all other features.")
              : matchingAddon
                ? t("Add the {{addon}} add-on to unlock this section.", { addon: matchingAddon.display_name })
                : t("Upgrade your plan to access this section.")}
          </p>

          {/* Upsell: recommend Full plan when it's better value */}
          {upsellSuggestion ? (
            <div className="w-full mb-4">
              <div className="border border-indigo-200 dark:border-indigo-700 rounded-lg p-4 bg-indigo-50/50 dark:bg-indigo-900/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-800/50">
                    <PiCrownLight className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t("PONS Full")}
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                      {t("Recommended")}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3 text-left leading-relaxed">
                  {t("Get every feature on the platform — AI Research, Tables, Review, Analytics, Templates, and more. No add-ons needed.")}
                </p>

                <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-md px-3 py-2 mb-3">
                  <div className="text-left">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("PONS Full")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      €{upsellSuggestion.fullPrice}/{t("mo")} {t("per seat")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("Starter + {{count}} add-ons", { count: upsellSuggestion.activeAddonCount + 1 })}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 line-through">
                      €{upsellSuggestion.projectedTotal}/{t("mo")}
                    </p>
                  </div>
                </div>

                {upsellSuggestion.savings > 0 && (
                  <p className="text-xs font-medium text-success-600 dark:text-success-400 mb-3 text-center">
                    {t("Save €{{amount}}/mo per seat by switching to Full", { amount: upsellSuggestion.savings.toFixed(0) })}
                  </p>
                )}

                <Button
                  variant="primary"
                  size="small"
                  onClick={handleUpgradeToFull}
                  disabled={upgradeLoading}
                  icon={<PiCrownLight className="w-4 h-4" />}
                  className="w-full"
                >
                  {upgradeLoading ? t("Upgrading...") : t("Upgrade to PONS Full")}
                </Button>
              </div>

              {/* Still allow adding just the addon */}
              {isOrgAdmin && matchingAddon && (
                <button
                  className="mt-2 w-full text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={handleAddAddon}
                  disabled={addonLoading}
                >
                  {addonLoading
                    ? t("Loading...")
                    : t("Or just add {{addon}} for €{{price}}/mo", { addon: matchingAddon.display_name, price: matchingAddon.price_monthly })}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Addon card when available (no upsell) */}
              {matchingAddon && (
                <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white dark:bg-gray-700 shadow-sm">
                      <PiPuzzlePieceLight className="w-4 h-4 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {matchingAddon.display_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        €{matchingAddon.price_monthly}/{t("mo")} {t("per seat")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action buttons — only when upsell card isn't showing */}
          {!upsellSuggestion && (
          <div className="flex items-center gap-3 w-full">
            {paymentPending ? (
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-warning-600 dark:text-warning-400 font-medium">
                  <Spinner size="glyph" className="!h-4 !w-4" />
                  {t("Processing payment...")}
                </div>
                <p className="text-xs text-gray-400 mt-1">{t("This usually takes a few seconds")}</p>
              </div>
            ) : isOrgAdmin && matchingAddon ? (
              <>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleAddAddon}
                  disabled={addonLoading}
                  icon={<PiPlusCircleLight className="w-4 h-4" />}
                  className="flex-1"
                >
                  {addonLoading ? t("Loading...") : t("Add Add-on")}
                </Button>
                <Button
                  variant="neutral"
                  size="small"
                  onClick={() => history.push("/account?tab=plans")}
                  title={t("View all plans")}
                >
                  <PiArrowRightLight className="w-4 h-4" />
                </Button>
              </>
            ) : requestSent ? (
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-success-600 dark:text-success-400 font-medium">
                  <PiCheckCircleLight className="w-4 h-4" />
                  {t("Request sent to your admin")}
                </div>
                <p className="text-xs text-gray-400 mt-1">{t("You'll be notified when it's reviewed")}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2 w-full">
                <Button
                  variant="primary"
                  size="small"
                  onClick={async () => {
                    if (requestSending || !section) return;
                    setRequestSending(true);
                    const success = await dispatch(
                      requestFeatureFromAdmin(section, t("I'd like access to {{section}}.", { section: sectionLabel }))
                    );
                    setRequestSending(false);
                    if (success) setRequestSent(true);
                  }}
                  disabled={requestSending}
                  icon={<PiEnvelopeSimpleLight className="w-4 h-4" />}
                  className="flex-1"
                >
                  {requestSending ? t("Sending...") : t("Request Access")}
                </Button>
                <button
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => history.push("/account?tab=plans")}
                >
                  {t("View Plans")}
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseOverlay;
